import DOMPurify from 'dompurify';
import styled from 'styled-components';

import {useMemo} from 'react';

import {suffixTestId, TestIdProps} from 'shared/web';

const ALLOWED_TAGS = ['p', 'strong', 'em', 'u', 's', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'br'];

interface RichTextDisplayProps extends TestIdProps {
  html: string;
}

export function RichTextDisplay(props: RichTextDisplayProps) {
  const sanitized = useMemo(
    () => DOMPurify.sanitize(props.html, {ALLOWED_TAGS, ALLOWED_ATTR: []}),
    [props.html]
  );

  return (
    <Wrapper
      data-testid={suffixTestId('rich-text', props)}
      // eslint-disable-next-line eslint-js/no-restricted-syntax, react/no-danger -- Input is sanitized by DOMPurify with an explicit tag allowlist
      dangerouslySetInnerHTML={{__html: sanitized}}
    />
  );
}

// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const Wrapper = styled.div`
  color: ${({theme}) => theme.colors.text.secondary};
  font-family: ${({theme}) => theme.fonts.body};
  font-size: ${({theme}) => theme.fontSizes.text.base};
  line-height: ${({theme}) => theme.lineHeights.text.base};
  letter-spacing: ${({theme}) => theme.letterSpacing.text.base};
  overflow-wrap: anywhere;

  p {
    margin: 0 0 8px;
  }

  h1 {
    color: ${({theme}) => theme.colors.text.primary};
    font-size: ${({theme}) => theme.fontSizes.heading.desktop[1]};
    line-height: ${({theme}) => theme.lineHeights.heading.desktop[1]};
    font-weight: ${({theme}) => theme.fontWeights.heading.default};
    margin: 16px 0 8px;
  }

  h2 {
    color: ${({theme}) => theme.colors.text.primary};
    font-size: ${({theme}) => theme.fontSizes.heading.desktop[2]};
    line-height: ${({theme}) => theme.lineHeights.heading.desktop[2]};
    font-weight: ${({theme}) => theme.fontWeights.heading.default};
    margin: 16px 0 8px;
  }

  h3 {
    color: ${({theme}) => theme.colors.text.primary};
    font-size: ${({theme}) => theme.fontSizes.heading.desktop[3]};
    line-height: ${({theme}) => theme.lineHeights.heading.desktop[3]};
    font-weight: ${({theme}) => theme.fontWeights.heading.default};
    margin: 12px 0 6px;
  }

  ul,
  ol {
    padding-left: 20px;
    margin: 0 0 8px;
  }

  li {
    margin-bottom: 4px;
  }

  strong {
    font-weight: ${({theme}) => theme.fontWeights.text.alternative};
  }

  em {
    font-style: italic;
  }

  u {
    text-decoration: underline;
  }

  s {
    text-decoration: line-through;
  }

  & > *:first-child {
    margin-top: 0;
  }

  & > *:last-child {
    margin-bottom: 0;
  }
`;
